/**
 * JuiceService — the orchestrator: device tier, degradation ladder,
 * reduced-motion, haptics. FIRST BLOOD §8 / JUICE-FLUTTER §7, web form.
 *
 * Degradation ladder (applied in order when frame budget is missed):
 *   TRIM: particle counts halve → SHIMMER disabled → RAY-WIPE becomes a
 *         static gradient.
 *   LITE: additionally, ceremony timelines compress 30% and particles ≤15.
 * CHOREOGRAPHY ORDER NEVER CHANGES — a degraded ceremony is shorter, not
 * different. The held breath is never compressed (ceremonies/engine.ts).
 *
 * prefers-reduced-motion: all primitives collapse to 200ms crossfades;
 * haptics are retained (the haptic carries the hit).
 */

import { JUICE_VERSION, LITE_COMPRESSION } from "./tokens.js";
import type { Tier } from "./types.js";
import { minTier } from "./types.js";
import type { HapticCue, VibrateFn } from "./haptics.js";
import { playHaptic } from "./haptics.js";

// ── Storage adapter ──────────────────────────────────────────────────────────
export interface JuiceStorage {
  get(key: string): string | null;
  set(key: string, value: string): void;
}

const STORAGE_KEY = "matchday.juice.tier";

function localStorageAdapter(): JuiceStorage {
  return {
    get(key) {
      try {
        const ls = (globalThis as { localStorage?: Storage }).localStorage;
        return ls ? ls.getItem(key) : null;
      } catch {
        return null;
      }
    },
    set(key, value) {
      try {
        const ls = (globalThis as { localStorage?: Storage }).localStorage;
        ls?.setItem(key, value);
      } catch {
        /* private mode etc. — probe again next launch */
      }
    },
  };
}

// ── Device probe (injectable) ────────────────────────────────────────────────
export interface ProbeEnv {
  /** requestAnimationFrame-alike */
  raf(cb: (t: number) => void): void;
  now(): number;
  deviceMemoryGb: number | undefined;
  hardwareConcurrency: number | undefined;
}

function defaultProbeEnv(): ProbeEnv {
  const g = globalThis as unknown as {
    requestAnimationFrame?: (cb: (t: number) => void) => number;
    performance?: { now(): number };
    navigator?: { deviceMemory?: number; hardwareConcurrency?: number };
  };
  return {
    raf: g.requestAnimationFrame
      ? (cb) => g.requestAnimationFrame!(cb)
      : (cb) => setTimeout(() => cb(Date.now()), 16),
    now: g.performance ? () => g.performance!.now() : () => Date.now(),
    deviceMemoryGb: g.navigator?.deviceMemory,
    hardwareConcurrency: g.navigator?.hardwareConcurrency,
  };
}

export function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0;
  const idx = Math.min(sorted.length - 1, Math.floor((p / 100) * sorted.length));
  return sorted[idx] ?? 0;
}

/** Pure classifier — exported for tests. Frame times in ms (p90). */
export function classifyTier(
  p90FrameMs: number,
  deviceMemoryGb: number | undefined,
  hardwareConcurrency: number | undefined,
): Tier {
  // rAF sampling verdict: 60fps budget 16.7ms; ≤20ms p90 ≈ holding 50-60fps.
  const fpsTier: Tier = p90FrameMs <= 20 ? "FULL" : p90FrameMs <= 34 ? "TRIM" : "LITE";
  // Hardware heuristics cap the ceiling (2GB reference device = TRIM ceiling).
  let hwCeiling: Tier = "FULL";
  if (deviceMemoryGb !== undefined && deviceMemoryGb <= 2) hwCeiling = "TRIM";
  if (hardwareConcurrency !== undefined && hardwareConcurrency <= 2) hwCeiling = "TRIM";
  if (deviceMemoryGb !== undefined && deviceMemoryGb <= 1) hwCeiling = "LITE";
  return minTier(fpsTier, hwCeiling);
}

export type TierProbe = (env: ProbeEnv, sampleMs: number) => Promise<Tier>;

/** ~2s rAF frame-time sampling + navigator heuristics. Injectable for tests. */
export const defaultProbe: TierProbe = (env, sampleMs) =>
  new Promise<Tier>((resolve) => {
    const deltas: number[] = [];
    const start = env.now();
    let last = start;
    const tick = () => {
      const t = env.now();
      const dt = t - last;
      last = t;
      if (dt > 0) deltas.push(dt);
      if (t - start >= sampleMs) {
        deltas.sort((a, b) => a - b);
        resolve(classifyTier(percentile(deltas, 90), env.deviceMemoryGb, env.hardwareConcurrency));
        return;
      }
      env.raf(tick);
    };
    env.raf(tick);
  });

// ── Reduced motion ───────────────────────────────────────────────────────────
function defaultReducedMotionQuery(): boolean {
  const g = globalThis as unknown as {
    matchMedia?: (q: string) => { matches: boolean };
  };
  try {
    return g.matchMedia ? g.matchMedia("(prefers-reduced-motion: reduce)").matches : false;
  } catch {
    return false;
  }
}

// ── Particle caps (FIRST BLOOD §8 / JUICE-FLUTTER §7) ───────────────────────
export type ParticleKind = "ceremony" | "ambient";

/** Hard caps: 60 ceremony / 20 ambient; halved in TRIM; ≤15 in LITE (15/5
 *  keeps the comp 3:1 ceremony:ambient ratio under the ≤15 law). */
export function particleCap(tier: Tier, kind: ParticleKind): number {
  const caps: Record<Tier, Record<ParticleKind, number>> = {
    FULL: { ceremony: 60, ambient: 20 },
    TRIM: { ceremony: 30, ambient: 10 },
    LITE: { ceremony: 15, ambient: 5 },
  };
  return caps[tier][kind];
}

// ── The service ──────────────────────────────────────────────────────────────
export interface JuiceServiceOptions {
  storage?: JuiceStorage;
  probe?: TierProbe;
  probeEnv?: ProbeEnv;
  probeSampleMs?: number;
  reducedMotionQuery?: () => boolean;
  vibrate?: VibrateFn;
  version?: string;
  initialTier?: Tier;
}

interface TierCache {
  tier: Tier;
  version: string;
}

export class JuiceService {
  private tier: Tier;
  private readonly storage: JuiceStorage;
  private readonly probe: TierProbe;
  private readonly probeEnvFactory: () => ProbeEnv;
  private readonly probeSampleMs: number;
  private readonly reducedQuery: () => boolean;
  private readonly vibrateFn: VibrateFn | undefined;
  private readonly version: string;
  private probing: Promise<Tier> | null = null;

  constructor(opts: JuiceServiceOptions = {}) {
    this.storage = opts.storage ?? localStorageAdapter();
    this.probe = opts.probe ?? defaultProbe;
    this.probeEnvFactory = opts.probeEnv ? () => opts.probeEnv! : defaultProbeEnv;
    this.probeSampleMs = opts.probeSampleMs ?? 2000;
    this.reducedQuery = opts.reducedMotionQuery ?? defaultReducedMotionQuery;
    this.vibrateFn = opts.vibrate;
    this.version = opts.version ?? JUICE_VERSION;
    this.tier = opts.initialTier ?? this.readCache()?.tier ?? "FULL";
  }

  private readCache(): TierCache | null {
    const raw = this.storage.get(STORAGE_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as Partial<TierCache>;
      if (
        (parsed.tier === "FULL" || parsed.tier === "TRIM" || parsed.tier === "LITE") &&
        typeof parsed.version === "string"
      ) {
        return { tier: parsed.tier, version: parsed.version };
      }
    } catch {
      /* corrupt cache — reprobe */
    }
    return null;
  }

  /**
   * Resolve the device tier: use the cached verdict when its version matches,
   * otherwise run the ~2s frame-time probe and cache the result.
   */
  async init(): Promise<Tier> {
    const cached = this.readCache();
    if (cached && cached.version === this.version) {
      this.tier = cached.tier;
      return this.tier;
    }
    if (!this.probing) {
      this.probing = this.probe(this.probeEnvFactory(), this.probeSampleMs).then((tier) => {
        this.tier = tier;
        this.storage.set(STORAGE_KEY, JSON.stringify({ tier, version: this.version } satisfies TierCache));
        this.probing = null;
        return tier;
      });
    }
    return this.probing;
  }

  getTier(): Tier {
    return this.tier;
  }

  /** Manual override (DUGOUT/dev toggles, tests). Persisted. */
  setTier(tier: Tier): void {
    this.tier = tier;
    this.storage.set(STORAGE_KEY, JSON.stringify({ tier, version: this.version } satisfies TierCache));
  }

  reducedMotion(): boolean {
    return this.reducedQuery();
  }

  particleCap(kind: ParticleKind): number {
    return particleCap(this.tier, kind);
  }

  /** SHIMMER is decorative — first thing the ladder cuts (off in TRIM+LITE). */
  shimmerEnabled(): boolean {
    return this.tier === "FULL" && !this.reducedMotion();
  }

  /** TRIM+LITE render the ray-wipe as a static gradient. */
  rayWipeMode(): "animated" | "static" {
    return this.tier === "FULL" ? "animated" : "static";
  }

  /**
   * LITE: timelines compress 30% (×0.7). NOTE: the ceremony engine applies
   * its own breath-preserving compression to whole timelines — this helper is
   * for standalone durations (action-internal animations).
   */
  compressMs(ms: number): number {
    return this.tier === "LITE" ? Math.round(ms * LITE_COMPRESSION) : ms;
  }

  /** Haptics — retained even under reduced motion (FIRST BLOOD §8). */
  haptic(cue: HapticCue | number[]): void {
    if (this.vibrateFn) playHaptic(cue, this.vibrateFn);
    else playHaptic(cue);
  }
}

/**
 * Shared default instance. React trees can override it via <JuiceProvider>;
 * everything in this package falls back to this singleton so juice works
 * outside React too.
 */
export const juice = new JuiceService();
